import { FC } from 'react';
import { useSubscribeToInvalidationState } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import LocalDiningIcon from '@mui/icons-material/LocalDining';
import Box from '@mui/material/Box';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableCell from 'components/TableCell';
import FabDialog from 'components/FabDialog';
import Row from 'components/CateringOptionsListRow';
import Add from 'components/CateringOptionsListAdd';
import CateringOptionsSync from 'components/CateringOptionsSync';

import { useAvoidSuspense, useDelete } from 'lib';

import { CateringOptionsList_data$key } from '__generated__/CateringOptionsList_data.graphql';
import Query, {
  CateringOptionsListRefetchQuery,
} from '__generated__/CateringOptionsListRefetchQuery.graphql';
import Sorting, { useSorting } from 'components/Sorting';

type Props = {
  data: CateringOptionsList_data$key;
};

const CateringOptionsList: FC<Props> = ({ data }) => {
  const [{ cateringOptions }, { refetch, loading }] = useAvoidSuspense<
    CateringOptionsListRefetchQuery,
    CateringOptionsList_data$key
  >(
    graphql`
      fragment CateringOptionsList_data on Query
      @refetchable(queryName: "CateringOptionsListRefetchQuery")
      @argumentDefinitions(
        first: { type: "Int", defaultValue: 2147483647 }
        after: { type: "String" }
      ) {
        cateringOptions(first: $first, after: $after) {
          __id
          edges {
            node {
              id
              ...CateringOptionsListRow_cateringOption
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    data,
    Query
  );

  const connectionID = cateringOptions?.__id;

  const [deleteCateringOption, deleting] = useDelete(connectionID);

  const [sort, sorting] = useSorting(
    graphql`
      mutation CateringOptionsListSortMutation(
        $positions: [EntityWithPositionInput!]!
      ) {
        updateCateringOptionPositions(positions: $positions)
      }
    `,
    connectionID
  );

  const ids = connectionID ? [connectionID] : [];
  useSubscribeToInvalidationState(ids, () => {
    refetch({});
  });

  const { edges, pageInfo } = cateringOptions ?? {};

  if (!edges) return null;

  return (
    <Box>
      <Sorting edges={edges} onSort={sort}>
        <Table
          toolbar={<CateringOptionsSync />}
          pageInfo={pageInfo ?? null}
          loading={loading || sorting || deleting}
          icon={<LocalDiningIcon />}
          head={
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Pavadimas</TableCell>
              <TableCell center>ETG ID</TableCell>
              <TableCell center>Rodoma paieškoje</TableCell>
              <TableCell></TableCell>
            </TableRow>
          }>
          {edges?.map(({ node }) => (
            <Row
              sortDisabled={sorting}
              cateringOption={node}
              key={node.id}
              onDelete={deleteCateringOption}
            />
          ))}
        </Table>
      </Sorting>

      {connectionID && (
        <FabDialog fullWidth maxWidth="sm">
          <Add connectionID={connectionID} />
        </FabDialog>
      )}
    </Box>
  );
};

export default CateringOptionsList;
